// termProgress — derives elapsed/remaining + humanised header strings +
// extension overlay positioning for the TermProgressBar component.
// Fill scale matches overlay scale (full possible term: base +
// every available extension period) so the violet fill butts up to
// the first overlay rather than leaving an unscaled gap. Defensive
// against malformed extension data: clamps negative widths to zero.

import type {
  ManagedContract,
  ExtensionOptions,
  DurationUnit,
} from "../../types";

export interface TermProgressResult {
  elapsedPct: number;
  elapsedHumanised: string;
  remainingHumanised: string;
  headerText: string;
  /** End date based on what has actually been exercised so far (base +
   *  exercised extensions). The "Completed N days ago" branch fires
   *  when today passes this. */
  effectiveEndDate: string;
  /** Latest possible end date (base + every available extension). The
   *  TermProgressBar footer surfaces this as "Possible end {date}" so
   *  the value matches the label. Equals effectiveEndDate when the
   *  contract has no extension options. */
  possibleEndDate: string;
}

export interface ExtensionOverlay {
  periodNumber: number;
  startPct: number;
  widthPct: number;
  status: "exercised" | "available" | "expired";
  startDate: string;
  endDate: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseLocalDate(dateStr: string): Date {
  return new Date(dateStr + "T00:00:00Z");
}

function utcDayDiff(a: Date, b: Date): number {
  const aUtc = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const bUtc = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  return Math.round((aUtc - bUtc) / MS_PER_DAY);
}

function addDuration(date: Date, value: number, unit: DurationUnit): Date {
  const r = new Date(date);
  if (unit === "years") r.setUTCFullYear(r.getUTCFullYear() + value);
  else if (unit === "months") r.setUTCMonth(r.getUTCMonth() + value);
  else r.setUTCDate(r.getUTCDate() + value);
  return r;
}

function extensionDays(opts: ExtensionOptions): number {
  const start = new Date("2026-01-01T00:00:00Z");
  const end = addDuration(start, opts.durationValue, opts.durationUnit);
  return Math.max(0, utcDayDiff(end, start));
}

function humaniseDays(days: number): string {
  if (days < 0) days = 0;
  // Weeks band only at "week-clean" small counts (7-28 inclusive). Outside
  // that band fall back to days for short durations and months/years for
  // longer ones — matches the wireframe's "30 days in" / "11 months
  // remaining" framing.
  if (days >= 7 && days <= 28 && days % 7 === 0) {
    const w = days / 7;
    return `${w} ${w === 1 ? "week" : "weeks"}`;
  }
  if (days < 60) return `${days} ${days === 1 ? "day" : "days"}`;
  if (days < 365) {
    const m = Math.round(days / 30);
    return `${m} ${m === 1 ? "month" : "months"}`;
  }
  const y = Math.round(days / 365);
  return `${y} ${y === 1 ? "year" : "years"}`;
}

function fmtUtcDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// endDate is taken as an explicit (narrowed) parameter rather than off the
// contract: ManagedContract reads can omit it, and computeTermProgress's
// nullish guard does not flow into helper bodies.
function effectiveEnd(endDate: string, opts: ExtensionOptions | undefined): string {
  if (!opts) return endDate;
  const exercised = opts.periods.filter((p) => p.exercised);
  if (exercised.length === 0) return endDate;
  const lastExercised = exercised[exercised.length - 1];
  if (lastExercised.newEndDate) return lastExercised.newEndDate;
  let end = parseLocalDate(endDate);
  for (let i = 0; i < exercised.length; i += 1) {
    end = addDuration(end, opts.durationValue, opts.durationUnit);
  }
  return fmtUtcDate(end);
}

function possibleEnd(endDate: string, opts: ExtensionOptions | undefined): string {
  if (!opts || opts.periods.length === 0) return endDate;
  let end = parseLocalDate(endDate);
  for (let i = 0; i < opts.periods.length; i += 1) {
    end = addDuration(end, opts.durationValue, opts.durationUnit);
  }
  return fmtUtcDate(end);
}

/** Null when the contract has no start or end date (sparse stored rows) —
 *  no term can be derived and the bar should not render. */
export function computeTermProgress(
  contract: ManagedContract,
  today: Date = new Date(),
): TermProgressResult | null {
  const { startDate, endDate } = contract;
  if (!startDate || !endDate) return null;
  const start = parseLocalDate(startDate);
  const effectiveEndStr = effectiveEnd(endDate, contract.extensionOptions);
  const possibleEndStr = possibleEnd(endDate, contract.extensionOptions);
  const effectiveEndD = parseLocalDate(effectiveEndStr);

  // Fill scale matches the overlay scale: the full possible-term
  // denominator (base + every available extension). This keeps the
  // violet fill butted up to the first overlay rather than leaving an
  // unscaled gap. Effective end (base + exercised) drives the
  // remaining/completed branches and the header text — those reflect
  // what's been actually committed to.
  const opts = contract.extensionOptions;
  const baseEnd = parseLocalDate(endDate);
  const baseDays = Math.max(1, utcDayDiff(baseEnd, start) + 1);
  const extDaysEach = opts ? extensionDays(opts) : 0;
  const periodCount = opts?.periods.length ?? 0;
  const totalScaleDays = baseDays + extDaysEach * periodCount;

  // Remaining math runs on effectiveEnd (the "I have N days left"
  // mental model is against what's actually committed, not optional
  // future extensions).
  const elapsedDays = utcDayDiff(today, start);
  const remainingDays = utcDayDiff(effectiveEndD, today) + 1;

  let elapsedPct: number;
  if (elapsedDays <= 0) elapsedPct = 0;
  else if (elapsedDays >= totalScaleDays) elapsedPct = 100;
  else elapsedPct = Math.round((elapsedDays / totalScaleDays) * 100);

  const elapsedHumanised = humaniseDays(Math.max(0, elapsedDays)) + " in";
  const remainingHumanised =
    humaniseDays(Math.max(0, remainingDays)) + " remaining";

  const daysUntilStart = -elapsedDays;
  const daysSinceEnd = utcDayDiff(today, effectiveEndD);

  let headerText: string;
  if (elapsedDays < 0) {
    headerText = `Starts in ${humaniseDays(daysUntilStart)}`;
  } else if (daysSinceEnd > 0) {
    headerText = `Completed ${humaniseDays(daysSinceEnd)} ago`;
  } else {
    headerText = `${elapsedHumanised} · ${remainingHumanised}`;
  }

  return {
    elapsedPct,
    elapsedHumanised,
    remainingHumanised,
    headerText,
    effectiveEndDate: effectiveEndStr,
    possibleEndDate: possibleEndStr,
  };
}

export function computeExtensionOverlays(
  contract: ManagedContract,
): ExtensionOverlay[] {
  const opts = contract.extensionOptions;
  if (!opts || opts.periods.length === 0) return [];

  // No term anchor on sparse rows → no overlays (matches the null term).
  if (!contract.startDate || !contract.endDate) return [];

  // IMPORTANT: this denominator MUST match the one computeTermProgress
  // uses for elapsedPct, otherwise the violet fill and the overlays
  // sit on different scales and a visible gap appears between them.
  const start = parseLocalDate(contract.startDate);
  const baseEnd = parseLocalDate(contract.endDate);
  const baseDays = Math.max(1, utcDayDiff(baseEnd, start) + 1);
  const extDays = Math.max(0, extensionDays(opts));

  const totalDays = baseDays + extDays * opts.periods.length;
  if (totalDays <= 0) return [];

  return opts.periods.map((p, i) => {
    const periodStartOffsetDays = baseDays + extDays * i;
    const periodEndOffsetDays = baseDays + extDays * (i + 1);
    const startPct = Math.max(
      0,
      Math.round((periodStartOffsetDays / totalDays) * 100),
    );
    const endPct = Math.max(
      0,
      Math.round((periodEndOffsetDays / totalDays) * 100),
    );
    const widthPct = Math.max(0, endPct - startPct);

    const periodStart = addDuration(
      baseEnd,
      opts.durationValue * i,
      opts.durationUnit,
    );
    const periodEnd = addDuration(
      baseEnd,
      opts.durationValue * (i + 1),
      opts.durationUnit,
    );

    return {
      periodNumber: p.periodNumber,
      startPct,
      widthPct,
      status: p.exercised ? "exercised" : "available",
      startDate: fmtUtcDate(periodStart),
      endDate: fmtUtcDate(periodEnd),
    };
  });
}
